import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Container,
  Paper,
  TextField,
  Button,
  Avatar,
  Divider,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Tabs,
  Tab,
  useTheme,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Modal,
  Backdrop,
  Fade,
} from '@mui/material';
import {
  Send as SendIcon,
  ArrowBack as ArrowBackIcon,
  AccountCircle,
  MenuOpen as MenuOpenIcon,
  AttachFile as AttachFileIcon,
  Menu as MenuIcon,
  ExitToApp as LogoutIcon,
  Event as EventIcon,
  LocalHospital as LocalHospitalIcon,
  Medication as MedicationIcon,
  Sick as SickIcon,
  HealthAndSafety as HealthAndSafetyIcon,
  Person as PersonIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { 
  doctor, 
  getConversationByPatientId,
  type Patient,
  type MedicalRecord,
  type MedicalRecordItem,
  type Message,
  type Conversation
} from '../utils/mockData';
import { getPatientByIdStore, getMedicalRecordByPatientIdStore } from '../utils/dataStore';
import { clearEmail } from '../utils/auth';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`medical-tabpanel-${index}`}
      aria-labelledby={`medical-tab-${index}`}
      style={{ height: '100%', overflowY: 'auto' }}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const getIconByType = (type: string) => {
  switch (type) {
    case 'session':
      return <EventIcon />;
    case 'treatment':
      return <LocalHospitalIcon />;
    case 'symptom':
      return <SickIcon />;
    case 'diagnosis':
      return <HealthAndSafetyIcon />;
    case 'medication':
      return <MedicationIcon />;
    default:
      return <EventIcon />;
  }
};

const getSeverityColor = (severity?: string) => {
  switch (severity) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'default';
  }
};

interface ImageLightboxProps {
  open: boolean;
  onClose: () => void;
  imageUrl: string;
  alt?: string;
}

const ImageLightbox: React.FC<ImageLightboxProps> = ({ open, onClose, imageUrl, alt }) => {
  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            maxWidth: '90vw',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            p: 1,
            outline: 'none',
            borderRadius: 1,
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <IconButton 
              onClick={onClose}
              sx={{
                position: 'absolute',
                right: -12,
                top: -12,
                bgcolor: 'background.paper',
                '&:hover': {
                  bgcolor: 'action.hover',
                },
                zIndex: 1,
              }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
            <img
              src={imageUrl}
              alt={alt || "Profilbild"}
              style={{
                maxWidth: '100%',
                maxHeight: 'calc(90vh - 32px)',
                display: 'block',
              }}
            />
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

const PatientInfoPanel: React.FC<{ patient: Patient }> = ({ patient }) => {
  const theme = useTheme();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  
  const handleOpenLightbox = () => {
    setLightboxOpen(true);
  };
  
  const handleCloseLightbox = () => {
    setLightboxOpen(false);
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Card elevation={0} sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Avatar 
              alt={patient.name}
              src={patient.avatar}
              sx={{ 
                width: 64, 
                height: 64, 
                mr: 2,
                cursor: 'pointer',
                '&:hover': {
                  opacity: 0.8,
                  boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
                },
                transition: 'all 0.2s'
              }}
              onClick={handleOpenLightbox}
            />
            <Box>
              <Typography variant="h6">{patient.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {patient.age} Jahre • {patient.gender}
              </Typography>
            </Box>
          </Box>
          
          <TableContainer component={Paper} elevation={0} variant="outlined">
            <Table size="small">
              <TableBody>
                <TableRow>
                  <TableCell component="th" variant="head" sx={{ fontWeight: 'bold', width: '40%' }}>
                    Adresse
                  </TableCell>
                  <TableCell>{patient.address || 'Keine Angabe'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" variant="head" sx={{ fontWeight: 'bold' }}>
                    Hausarzt
                  </TableCell>
                  <TableCell>{patient.primaryPhysician || 'Keine Angabe'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" variant="head" sx={{ fontWeight: 'bold' }}>
                    Versichertennummer
                  </TableCell>
                  <TableCell>{patient.insuranceNumber || 'Keine Angabe'}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell component="th" variant="head" sx={{ fontWeight: 'bold' }}>
                    Blutgruppe
                  </TableCell>
                  <TableCell>{patient.bloodType || 'Keine Angabe'}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      
      {/* Lightbox */}
      <ImageLightbox 
        open={lightboxOpen}
        onClose={handleCloseLightbox}
        imageUrl={patient.avatar || ''}
        alt={`${patient.name} Profilbild`}
      />
    </Box>
  );
};

const ChatPage: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [tabValue, setTabValue] = useState(0);
  const [messageText, setMessageText] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [patient, setPatient] = useState<Patient | null>(null);

  // Load patient and chat data
  useEffect(() => {
    if (patientId) {
      const foundPatient = getPatientByIdStore(patientId);
      if (foundPatient) {
        setPatient(foundPatient);
        const conversation = getConversationByPatientId(patientId);
        if (conversation) {
          setMessages(conversation.messages);
        }
      } else {
        // Patient not found, redirect to overview
        navigate('/overview');
      }
    }
  }, [patientId, navigate]);

  // Scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Get medical record for current patient
  const medicalRecord = patientId ? getMedicalRecordByPatientIdStore(patientId) : undefined;

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    clearEmail();
    navigate('/');
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (messageText.trim() && patient) {
      const newMessage: Message = {
        id: `msg-new-${Date.now()}`,
        senderId: doctor.id,
        receiverId: patient.id,
        content: messageText,
        timestamp: new Date().toISOString(),
        read: false,
      };
      setMessages([...messages, newMessage]);
      setMessageText('');
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleBackToOverview = () => {
    navigate('/overview');
  };

  const formatMessageTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatRecordDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Filter medical records based on tab index
  const getFilteredRecords = () => {
    if (!medicalRecord) return [];
    
    if (tabValue === 1) { 
      return [...medicalRecord.records].sort((a, b) => 
        new Date(b.date).getTime() - new Date(a.date).getTime()
      );
    } else {
      const type = tabValue === 2 ? 'session' : 
                  tabValue === 3 ? 'treatment' : 
                  tabValue === 4 ? 'diagnosis' : 
                  'medication';
      
      return medicalRecord.records
        .filter(record => record.type === type)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      {/* App Bar */}
      <AppBar position="static" color="primary" elevation={0}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            aria-label="back"
            onClick={handleBackToOverview}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          {patient && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Avatar
                alt={patient.name}
                src={patient.avatar}
                sx={{ width: 40, height: 40, mr: 2 }}
              />
              <Box>
                <Typography variant="subtitle1">{patient.name}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {patient.age} Jahre • {patient.gender}
                </Typography>
              </Box>
            </Box>
          )}
          
          <Box sx={{ flexGrow: 1 }} />
          
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 2 }}>
              {doctor.name}
            </Typography>
            <IconButton
              size="large"
              edge="end"
              color="inherit"
              aria-label="account"
              onClick={handleOpenMenu}
            >
              <Avatar
                alt={doctor.name}
                src={doctor.avatar}
                sx={{ width: 32, height: 32 }}
              />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleCloseMenu}>Profil</MenuItem>
              <MenuItem onClick={handleCloseMenu}>Einstellungen</MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
                Abmelden
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
        {/* Chat Area */}
        <Box sx={{ 
          width: '70%',
          display: 'flex', 
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
        }}>
          {/* Messages */}
          <Box sx={{ 
            flexGrow: 1, 
            p: 2, 
            overflowY: 'auto', 
            backgroundColor: theme.palette.background.default 
          }}>
            {messages.map((message) => {
              const isFromDoctor = message.senderId === doctor.id;
              return (
                <Box 
                  key={message.id}
                  sx={{ 
                    display: 'flex',
                    justifyContent: isFromDoctor ? 'flex-end' : 'flex-start',
                    mb: 2,
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    maxWidth: '75%',
                    flexDirection: isFromDoctor ? 'row-reverse' : 'row',
                  }}>
                    <Avatar
                      sx={{ 
                        width: 36, 
                        height: 36,
                        m: 0.5,
                        ml: isFromDoctor ? 1 : 0,
                        mr: isFromDoctor ? 0 : 1,
                      }}
                      alt={isFromDoctor ? doctor.name : patient?.name}
                      src={isFromDoctor ? doctor.avatar : patient?.avatar}
                    />
                    <Box
                      sx={{
                        bgcolor: isFromDoctor 
                          ? theme.palette.primary.main 
                          : theme.palette.background.paper,
                        color: isFromDoctor ? 'white' : 'inherit',
                        borderRadius: 2,
                        p: 2,
                        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
                      }}
                    >
                      <Typography variant="body1">{message.content}</Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block', 
                          textAlign: isFromDoctor ? 'right' : 'left',
                          color: isFromDoctor ? 'rgba(255, 255, 255, 0.7)' : 'text.secondary',
                          mt: 0.5,
                        }}
                      >
                        {formatMessageTime(message.timestamp)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>

          {/* Message Input */}
          <Paper 
            component="form" 
            onSubmit={handleSendMessage}
            sx={{ 
              p: 2, 
              display: 'flex', 
              alignItems: 'center',
              borderTop: `1px solid ${theme.palette.divider}`,
              boxShadow: 'none',
            }}
          >
            <IconButton size="small" sx={{ mr: 1 }}>
              <AttachFileIcon />
            </IconButton>
            <TextField
              fullWidth
              placeholder="Nachricht eingeben..."
              variant="outlined"
              size="small"
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              sx={{ mr: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              endIcon={<SendIcon />}
              disabled={!messageText.trim()}
              type="submit"
            >
              Senden
            </Button>
          </Paper>
        </Box>

        {/* Medical Records Sidebar */}
        <Box
          sx={{
            width: '30%',
            bgcolor: 'background.paper',
            borderLeft: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ p: 2, pb: 1 }}>
              Medizinische Akten
            </Typography>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="medical record tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Allgemeine Infos" id="medical-tab-0" aria-controls="medical-tabpanel-0" icon={<PersonIcon />} iconPosition="start" />
              <Tab label="Alle Einträge" id="medical-tab-1" aria-controls="medical-tabpanel-1" />
              <Tab label="Sitzungen" id="medical-tab-2" aria-controls="medical-tabpanel-2" />
              <Tab label="Behandlungen" id="medical-tab-3" aria-controls="medical-tabpanel-3" />
              <Tab label="Diagnosen" id="medical-tab-4" aria-controls="medical-tabpanel-4" />
              <Tab label="Medikamente" id="medical-tab-5" aria-controls="medical-tabpanel-5" />
            </Tabs>
          </Box>

          {/* Tab Panels */}
          <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
            <TabPanel value={tabValue} index={0}>
              {patient && <PatientInfoPanel patient={patient} />}
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              {getFilteredRecords().length > 0 ? (
                <List sx={{ py: 0 }}>
                  {getFilteredRecords().map((record: MedicalRecordItem) => (
                    <Card key={record.id} sx={{ mb: 2 }}>
                      <CardHeader
                        avatar={
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {getIconByType(record.type)}
                          </Avatar>
                        }
                        title={record.title}
                        subheader={formatRecordDate(record.date)}
                        action={
                          record.severity && (
                            <Chip 
                              label={record.severity} 
                              color={getSeverityColor(record.severity) as any}
                              size="small"
                              sx={{ mt: 1, mr: 1 }}
                            />
                          )
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                          {record.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Keine Einträge gefunden.
                  </Typography>
                </Box>
              )}
            </TabPanel>
            <TabPanel value={tabValue} index={2}>
              {getFilteredRecords().length > 0 ? (
                <List sx={{ py: 0 }}>
                  {getFilteredRecords().map((record: MedicalRecordItem) => (
                    <Card key={record.id} sx={{ mb: 2 }}>
                      <CardHeader
                        avatar={
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {getIconByType(record.type)}
                          </Avatar>
                        }
                        title={record.title}
                        subheader={formatRecordDate(record.date)}
                        action={
                          record.severity && (
                            <Chip 
                              label={record.severity} 
                              color={getSeverityColor(record.severity) as any}
                              size="small"
                              sx={{ mt: 1, mr: 1 }}
                            />
                          )
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                          {record.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Keine Einträge gefunden.
                  </Typography>
                </Box>
              )}
            </TabPanel>
            <TabPanel value={tabValue} index={3}>
              {getFilteredRecords().length > 0 ? (
                <List sx={{ py: 0 }}>
                  {getFilteredRecords().map((record: MedicalRecordItem) => (
                    <Card key={record.id} sx={{ mb: 2 }}>
                      <CardHeader
                        avatar={
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {getIconByType(record.type)}
                          </Avatar>
                        }
                        title={record.title}
                        subheader={formatRecordDate(record.date)}
                        action={
                          record.severity && (
                            <Chip 
                              label={record.severity} 
                              color={getSeverityColor(record.severity) as any}
                              size="small"
                              sx={{ mt: 1, mr: 1 }}
                            />
                          )
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                          {record.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Keine Einträge gefunden.
                  </Typography>
                </Box>
              )}
            </TabPanel>
            <TabPanel value={tabValue} index={4}>
              {getFilteredRecords().length > 0 ? (
                <List sx={{ py: 0 }}>
                  {getFilteredRecords().map((record: MedicalRecordItem) => (
                    <Card key={record.id} sx={{ mb: 2 }}>
                      <CardHeader
                        avatar={
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {getIconByType(record.type)}
                          </Avatar>
                        }
                        title={record.title}
                        subheader={formatRecordDate(record.date)}
                        action={
                          record.severity && (
                            <Chip 
                              label={record.severity} 
                              color={getSeverityColor(record.severity) as any}
                              size="small"
                              sx={{ mt: 1, mr: 1 }}
                            />
                          )
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                          {record.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Keine Einträge gefunden.
                  </Typography>
                </Box>
              )}
            </TabPanel>
            <TabPanel value={tabValue} index={5}>
              {getFilteredRecords().length > 0 ? (
                <List sx={{ py: 0 }}>
                  {getFilteredRecords().map((record: MedicalRecordItem) => (
                    <Card key={record.id} sx={{ mb: 2 }}>
                      <CardHeader
                        avatar={
                          <Avatar sx={{ bgcolor: theme.palette.primary.main }}>
                            {getIconByType(record.type)}
                          </Avatar>
                        }
                        title={record.title}
                        subheader={formatRecordDate(record.date)}
                        action={
                          record.severity && (
                            <Chip 
                              label={record.severity} 
                              color={getSeverityColor(record.severity) as any}
                              size="small"
                              sx={{ mt: 1, mr: 1 }}
                            />
                          )
                        }
                      />
                      <CardContent sx={{ pt: 0 }}>
                        <Typography variant="body2" color="text.secondary">
                          {record.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </List>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography color="text.secondary">
                    Keine Einträge gefunden.
                  </Typography>
                </Box>
              )}
            </TabPanel>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatPage;
